import { Table, Form, Spinner, Alert, Badge, Pagination } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SimpleBarReact from 'simplebar-react';
import { SORT_OPTIONS } from '../hooks/useFCRecommendations';

const priorityBadgeVariant = {
  'URGENT': 'danger',
  'HIGH': 'warning',
  'MEDIUM': 'info',
  'LOW': 'secondary',
  'OK': 'success'
};

const strategyBadgeVariant = {
  'AGGRESSIVE': 'success',
  'MODERATE': 'info',
  'BALANCED': 'primary',
  'CONSERVATIVE': 'warning',
  'PROFILE_BASED': 'secondary'
};

const temperatureBadgeVariant = {
  'AMBIENT': 'light',
  'REFRIGERATED': 'info',
  'FROZEN': 'primary'
};

const serviceLevelBadgeVariant = {
  'GROUND': 'secondary',
  'THREE_DAY': 'info',
  'TWO_DAY': 'warning',
  'NEXT_DAY': 'danger'
};

const serviceLevelLabel = {
  'GROUND': 'Ground',
  'THREE_DAY': '3-Day',
  'TWO_DAY': '2-Day',
  'NEXT_DAY': 'Next Day'
};

const recommendationIcon = {
  'Increase': { icon: 'arrow-up', color: 'danger' },
  'Decrease': { icon: 'arrow-down', color: 'success' },
  'No Change': { icon: 'minus', color: 'secondary' }
};

// Mapping of column headers to sort keys (primary = first click, alternate = second click to toggle)
const COLUMN_SORT_MAP = {
  sku: { primary: 'sku', alternate: 'skuDesc' },
  currentFC: { primary: 'suggestedFCAsc', alternate: 'suggestedFC' },
  avgCost: { primary: 'avgCost', alternate: 'suggestedFCAsc' },
  suggestedFC: { primary: 'suggestedFC', alternate: 'suggestedFCAsc' },
  delta: { primary: 'deltaAbs', alternate: 'delta' },
  priority: { primary: 'priority', alternate: 'priorityDesc' },
  sampleSize: { primary: 'sampleSize', alternate: 'sampleSizeAsc' }
};

// Format relative time for "Last Order" display
function formatRelativeTime(date) {
  if (!date) return null;
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Helper to get sort icon
function getSortIcon(columnKey, currentSortKey) {
  const columnConfig = COLUMN_SORT_MAP[columnKey];
  if (!columnConfig) return null;

  const sortOption = SORT_OPTIONS[currentSortKey];
  if (!sortOption) return null;

  // Check if this column is currently sorted
  const isPrimary = currentSortKey === columnConfig.primary;
  const isAlternate = columnConfig.alternate && currentSortKey === columnConfig.alternate;

  if (!isPrimary && !isAlternate) return null;

  return sortOption.direction === 'desc' ? 'sort-down' : 'sort-up';
}

// Sortable header component
function SortableHeader({ children, columnKey, currentSortKey, onSortChange, align = 'start', className = '' }) {
  const columnConfig = COLUMN_SORT_MAP[columnKey];
  if (!columnConfig || !onSortChange) {
    return <th className={`align-middle text-${align} ${className}`}>{children}</th>;
  }

  const sortIcon = getSortIcon(columnKey, currentSortKey);
  const isActive = sortIcon !== null;

  const handleClick = () => {
    // If already sorted by primary, switch to alternate (if exists) or keep same
    // If sorted by alternate, switch to primary
    // Otherwise, set to primary
    if (currentSortKey === columnConfig.primary && columnConfig.alternate) {
      onSortChange(columnConfig.alternate);
    } else if (currentSortKey === columnConfig.alternate) {
      onSortChange(columnConfig.primary);
    } else {
      onSortChange(columnConfig.primary);
    }
  };

  return (
    <th
      className={`align-middle text-${align} cursor-pointer user-select-none ${className} ${isActive ? 'text-primary' : ''}`}
      onClick={handleClick}
      style={{ cursor: 'pointer' }}
      title={`Sort by ${children}`}
    >
      {children}
      {sortIcon && (
        <FontAwesomeIcon icon={sortIcon} className="ms-1 fs--2" />
      )}
      {!sortIcon && (
        <FontAwesomeIcon icon="sort" className="ms-1 fs--2 text-400" />
      )}
    </th>
  );
}

/**
 * Get the appropriate FC value based on selected method
 * @param {Object} row - The data row
 * @param {string} fcMethod - 'SS1' | 'SSN' | 'MS' | 'BLENDED'
 * @returns {number|null} The FC value to display
 */
function getFCByMethod(row, fcMethod) {
  switch (fcMethod) {
    case 'SSN':
      // SSN per-unit P75 + handling from fcOptions
      return row.fcOptions?.ssn?.fc ?? null;
    case 'MS':
      // MS allocated P75 + handling from fcOptions
      return row.fcOptions?.ms?.fc ?? null;
    case 'BLENDED':
      // Blended FC (order-mix weighted)
      return row.blendedFC ?? row.fcOptions?.blended?.fc ?? null;
    case 'SS1':
    default:
      // Default SS1 suggested FC
      return row.suggestedFC;
  }
}

/**
 * Get the FC method label for display
 */
function getFCMethodLabel(fcMethod) {
  const labels = {
    'SS1': 'SS1',
    'SSN': 'SSN',
    'MS': 'MS',
    'BLENDED': 'Blended'
  };
  return labels[fcMethod] || 'SS1';
}

export default function FCTable({
  data = [],
  loading = false,
  error = null,
  selectedRows = [],
  onRowClick,
  onSelectAll,
  onRowSelect,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  sortKey = 'priority',
  onSortChange,
  fcMethod = 'SS1'
}) {
  const allSelected = data.length > 0 && selectedRows.length === data.length;
  const someSelected = selectedRows.length > 0 && selectedRows.length < data.length;

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Loading recommendations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-3">
        <FontAwesomeIcon icon="exclamation-circle" className="me-2" />
        Error loading data: {error.message}
      </Alert>
    );
  }

  if (data.length === 0) {
    return (
      <Alert variant="info" className="m-3">
        <FontAwesomeIcon icon="info-circle" className="me-2" />
        No recommendations found. Try adjusting your filters or import current FC data.
      </Alert>
    );
  }

  return (
    <>
      <SimpleBarReact>
        <Table hover className="fs--1 mb-0 fc-table">
          <thead className="bg-200 text-900">
            <tr>
              <th className="align-middle" style={{ width: '40px' }}>
                <Form.Check
                  type="checkbox"
                  checked={allSelected}
                  ref={input => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={(e) => onSelectAll(e.target.checked)}
                />
              </th>
              <SortableHeader columnKey="sku" currentSortKey={sortKey} onSortChange={onSortChange}>
                SKU
              </SortableHeader>
              <th className="align-middle">Product</th>
              <SortableHeader columnKey="currentFC" currentSortKey={sortKey} onSortChange={onSortChange} align="end">
                Current FC
              </SortableHeader>
              <SortableHeader columnKey="avgCost" currentSortKey={sortKey} onSortChange={onSortChange} align="end">
                Avg Cost
              </SortableHeader>
              <SortableHeader columnKey="suggestedFC" currentSortKey={sortKey} onSortChange={onSortChange} align="end">
                Suggested FC
                {fcMethod !== 'SS1' && (
                  <Badge bg="light" text="dark" className="ms-1 fw-normal fs--2">
                    {getFCMethodLabel(fcMethod)}
                  </Badge>
                )}
              </SortableHeader>
              <SortableHeader columnKey="delta" currentSortKey={sortKey} onSortChange={onSortChange} align="end">
                Delta
              </SortableHeader>
              <SortableHeader columnKey="priority" currentSortKey={sortKey} onSortChange={onSortChange} align="center">
                Priority
              </SortableHeader>
              <SortableHeader columnKey="sampleSize" currentSortKey={sortKey} onSortChange={onSortChange} align="end">
                Samples
              </SortableHeader>
              <th className="align-middle text-center" style={{ width: '90px' }}>Strategy</th>
              <th className="align-middle text-center" style={{ width: '80px' }}>Category</th>
              <th className="align-middle text-center" style={{ width: '80px' }}>Service</th>
              <th className="align-middle text-end" style={{ width: '100px' }}>Last Order</th>
              <th className="align-middle text-center" style={{ width: '40px' }}></th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => {
              const isSelected = selectedRows.includes(row.sku);
              const recConfig = recommendationIcon[row.recommendation] || recommendationIcon['No Change'];

              // Get FC value based on selected method
              const displayFC = getFCByMethod(row, fcMethod);
              const hasDisplayFC = displayFC !== null && displayFC !== undefined;

              // Calculate delta based on selected FC method
              const methodDelta = hasDisplayFC && row.currentFC != null
                ? displayFC - row.currentFC
                : null;

              // Determine recommendation icon based on method-specific delta
              const methodRecConfig = methodDelta !== null
                ? methodDelta > 0.5 ? recommendationIcon['Increase']
                : methodDelta < -0.5 ? recommendationIcon['Decrease']
                : recommendationIcon['No Change']
                : recConfig;

              return (
                <tr
                  key={row.sku}
                  className={`cursor-pointer ${isSelected ? 'table-active' : ''}`}
                  onClick={() => onRowClick(row.sku)}
                >
                  <td className="align-middle" onClick={(e) => e.stopPropagation()}>
                    <Form.Check
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => onRowSelect(row.sku, e.target.checked)}
                    />
                  </td>
                  <td className="align-middle">
                    <span className="fw-semi-bold text-primary">{row.sku}</span>
                  </td>
                  <td className="align-middle text-truncate" style={{ maxWidth: '200px' }}>
                    {row.title || '-'}
                  </td>
                  <td className="align-middle text-end">
                    {row.currentFC !== null && row.currentFC !== undefined
                      ? `$${row.currentFC.toFixed(2)}`
                      : <span className="text-danger fw-semi-bold">Not Set</span>
                    }
                  </td>
                  <td className="align-middle text-end">
                    ${row.avgCost?.toFixed(2) || '-'}
                  </td>
                  <td className="align-middle text-end fw-semi-bold">
                    {hasDisplayFC
                      ? `$${displayFC.toFixed(2)}`
                      : fcMethod === 'SS1'
                        ? `$${row.suggestedFC?.toFixed(2) || '-'}`
                        : <span className="text-muted fs--2">N/A</span>
                    }
                  </td>
                  <td className={`align-middle text-end text-${methodRecConfig.color}`}>
                    <FontAwesomeIcon icon={methodRecConfig.icon} className="me-1" />
                    {methodDelta != null
                      ? (methodDelta > 0 ? '+$' : methodDelta < 0 ? '-$' : '$') + Math.abs(methodDelta).toFixed(2)
                      : '-'}
                  </td>
                  <td className="align-middle text-center">
                    <Badge bg={priorityBadgeVariant[row.priority] || 'secondary'}>
                      {row.priority}
                    </Badge>
                  </td>
                  <td className="align-middle text-end">
                    <SampleSizeIndicator
                      sampleSize={row.sampleSize}
                      liveSampleSize={row.liveSampleSize}
                      hasLiveData={row.hasLiveData}
                    />
                  </td>
                  <td className="align-middle text-center">
                    <StrategyIndicator
                      strategy={row.strategy}
                      ss1Count={row.ss1Count}
                      economyPct={row.economyPct}
                      temperatureCategory={row.temperatureCategory}
                    />
                  </td>
                  <td className="align-middle text-center">
                    <TemperatureCategoryBadge category={row.temperatureCategory} />
                  </td>
                  <td className="align-middle text-center">
                    <ServiceLevelBadge
                      dominantLevel={row.dominantServiceLevel}
                      groundPct={row.groundPct}
                      groundCount={row.groundCount}
                      expeditedCount={row.expeditedCount}
                    />
                  </td>
                  <td className="align-middle text-end">
                    <LastOrderIndicator
                      lastOrderAt={row.liveLastOrderAt}
                      hasLiveData={row.hasLiveData}
                    />
                  </td>
                  <td className="align-middle text-center">
                    <FontAwesomeIcon
                      icon="chevron-right"
                      className="text-400"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </SimpleBarReact>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top">
          <span className="text-muted fs--1">
            Page {currentPage} of {totalPages}
          </span>
          <Pagination size="sm" className="mb-0">
            <Pagination.First
              disabled={currentPage === 1}
              onClick={() => onPageChange(1)}
            />
            <Pagination.Prev
              disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}
            />

            {/* Page numbers */}
            {generatePageNumbers(currentPage, totalPages).map((page, idx) => (
              page === '...' ? (
                <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled />
              ) : (
                <Pagination.Item
                  key={page}
                  active={page === currentPage}
                  onClick={() => onPageChange(page)}
                >
                  {page}
                </Pagination.Item>
              )
            ))}

            <Pagination.Next
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(currentPage + 1)}
            />
            <Pagination.Last
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(totalPages)}
            />
          </Pagination>
        </div>
      )}
    </>
  );
}

function SampleSizeIndicator({ sampleSize, liveSampleSize, hasLiveData }) {
  const batchSize = sampleSize || 0;
  const liveSize = liveSampleSize || 0;
  const displaySize = hasLiveData ? liveSize : batchSize;

  let variant = 'secondary';
  let icon = 'exclamation-triangle';

  if (displaySize >= 50) {
    variant = 'success';
    icon = 'check-circle';
  } else if (displaySize >= 20) {
    variant = 'primary';
    icon = 'check';
  } else if (displaySize >= 10) {
    variant = 'warning';
    icon = 'exclamation';
  } else {
    variant = 'danger';
    icon = 'exclamation-triangle';
  }

  const confidenceText = displaySize >= 50 ? 'High confidence' :
                         displaySize >= 20 ? 'Good confidence' :
                         displaySize >= 10 ? 'Medium confidence' : 'Low confidence - need more data';

  return (
    <span
      className={`text-${variant}`}
      title={`${confidenceText}${hasLiveData ? ' (Live)' : ' (Batch)'}`}
    >
      <FontAwesomeIcon icon={icon} className="me-1 fs--2" />
      {displaySize.toLocaleString()}
      {hasLiveData && (
        <FontAwesomeIcon
          icon="bolt"
          className="ms-1 fs--2 text-warning"
          title="Live data"
        />
      )}
    </span>
  );
}

function StrategyIndicator({ strategy, ss1Count, economyPct, temperatureCategory }) {
  if (!strategy) {
    return <span className="text-muted fs--2">-</span>;
  }

  const variant = strategyBadgeVariant[strategy] || 'secondary';

  // Build tooltip with context
  const tooltipParts = [];
  if (ss1Count > 0) tooltipParts.push(`${ss1Count} SS1 orders`);
  if (economyPct > 0) tooltipParts.push(`${economyPct.toFixed(0)}% economy`);
  if (temperatureCategory && temperatureCategory !== 'AMBIENT') {
    tooltipParts.push(temperatureCategory);
  }
  const tooltip = tooltipParts.length > 0 ? tooltipParts.join(', ') : strategy;

  return (
    <Badge
      bg={variant}
      className="fs--2"
      title={tooltip}
    >
      {strategy.slice(0, 4)}
    </Badge>
  );
}

function LastOrderIndicator({ lastOrderAt, hasLiveData }) {
  if (!hasLiveData || !lastOrderAt) {
    return <span className="text-muted fs--2">-</span>;
  }

  const relativeTime = formatRelativeTime(lastOrderAt);
  const isRecent = (new Date() - lastOrderAt) < 1000 * 60 * 60; // Less than 1 hour

  return (
    <span
      className={`fs--2 ${isRecent ? 'text-success fw-semi-bold' : 'text-muted'}`}
      title={lastOrderAt.toLocaleString()}
    >
      {isRecent && <FontAwesomeIcon icon="circle" className="me-1 fs--3 text-success" />}
      {relativeTime}
    </span>
  );
}

function TemperatureCategoryBadge({ category }) {
  if (!category || category === 'AMBIENT') {
    return <span className="text-muted fs--2">Ambient</span>;
  }

  const variant = temperatureBadgeVariant[category] || 'light';
  const materialCost = category === 'FROZEN' ? '+$25' : category === 'REFRIGERATED' ? '+$15' : '';

  return (
    <Badge
      bg={variant}
      className="fs--2"
      title={`${category} - Material cost: ${materialCost || '$0'}`}
    >
      {category === 'FROZEN' && <FontAwesomeIcon icon="snowflake" className="me-1" />}
      {category === 'REFRIGERATED' && <FontAwesomeIcon icon="temperature-low" className="me-1" />}
      {category.slice(0, 4)}
    </Badge>
  );
}

function ServiceLevelBadge({ dominantLevel, groundPct, groundCount, expeditedCount }) {
  if (!dominantLevel) {
    return <span className="text-muted fs--2">-</span>;
  }

  const variant = serviceLevelBadgeVariant[dominantLevel] || 'secondary';
  const label = serviceLevelLabel[dominantLevel] || dominantLevel;
  const total = (groundCount || 0) + (expeditedCount || 0);

  // Build tooltip
  const tooltipParts = [];
  if (groundCount > 0) tooltipParts.push(`Ground: ${groundCount}`);
  if (expeditedCount > 0) tooltipParts.push(`Expedited: ${expeditedCount}`);
  if (groundPct !== undefined) tooltipParts.push(`${groundPct}% ground`);
  const tooltip = tooltipParts.length > 0 ? tooltipParts.join(', ') : label;

  return (
    <Badge
      bg={variant}
      className="fs--2"
      title={tooltip}
    >
      {dominantLevel !== 'GROUND' && <FontAwesomeIcon icon="shipping-fast" className="me-1" />}
      {label}
    </Badge>
  );
}

function generatePageNumbers(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages = [];

  // Always show first page
  pages.push(1);

  if (current > 3) {
    pages.push('...');
  }

  // Show pages around current
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }

  if (current < total - 2) {
    pages.push('...');
  }

  // Always show last page
  if (total > 1) {
    pages.push(total);
  }

  return pages;
}
